import axios from 'axios';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { ServiceStatus } from '@/models/enums/service';
import Service from '@/services';
import { OffPlanFilterModel } from '@/models/classes/plan/off_plan/filter';
import { OffPlanCustomerModel } from '@/models/classes/plan/off_plan/customer';
import { VerifyOffPlanModel } from '@/models/classes/plan/off_plan';
import { mergeObjects } from '@/utils/utils';

const service = new Service.OffPlanService();

// Interface
interface OffPlanState {
  customer: {
    network: {
      code: ServiceStatus;
      error?: string;
    };
    data?: OffPlanCustomerModel[];
    total?: number;
  };
  filter: {
    network: {
      code: ServiceStatus;
      error?: string;
    };
    data?: OffPlanFilterModel[];
    total?: number;
  };
  verify: {
    network: {
      code: ServiceStatus;
      error?: string;
    };
    data?: VerifyOffPlanModel;
  };
}

// Initialize State
const initialState: OffPlanState = {
  customer: {
    network: {
      code: ServiceStatus.idle,
      error: undefined,
    },
    data: undefined,
    total: undefined,
  },
  filter: {
    network: {
      code: ServiceStatus.idle,
      error: undefined,
    },
    data: undefined,
    total: undefined,
  },
  verify: {
    network: {
      code: ServiceStatus.idle,
      error: undefined,
    },
    data: undefined,
  },
};

// Reducer
export const slice = createSlice({
  name: 'OffPlan',
  initialState,
  reducers: {
    reset: () => initialState,
    patch: (state, action) => mergeObjects({ ...state }, action.payload),
  },
  extraReducers: (builder) => {
    // Fetch Filter
    builder.addCase(onFetchFilter.pending, (state) => {
      state.filter.network.code = ServiceStatus.loading;
    });
    builder.addCase(onFetchFilter.fulfilled, (state, action) => {
      state.filter.network.code = ServiceStatus.succeeded;
      if (action.payload != null) {
        state.filter.data = action.payload.data;
        state.filter.total = action.payload.total;
      }
    });
    builder.addCase(onFetchFilter.rejected, (state, action) => {
      state.filter.network.code = ServiceStatus.failed;
      state.filter.network.error = (action.payload as string) || 'Failed to fetch data';
    });

    // Fetch Customer
    builder.addCase(onFetchCustomerOffPlan.pending, (state, action) => {
      state.customer.network.code = ServiceStatus.loading;
      const param = action.meta.arg;
      if (!param || !param.offset || param.offset === 0) {
        state.customer.data = undefined;
      }
    });
    builder.addCase(onFetchCustomerOffPlan.fulfilled, (state, action) => {
      if (action.payload != null && action.payload.apiData != null) {
        const { apiData, param } = action.payload;
        if (param?.offset && param.offset > 0) {
          if (state.customer.data) {
            state.customer.data = [...state.customer.data, ...(apiData.data ?? [])];
          } else {
            state.customer.data = apiData.data;
          }
        } else {
          state.customer.data = apiData.data;
        }
        state.customer.total = apiData.total;
      }
      state.customer.network.code = ServiceStatus.succeeded;
    });
    builder.addCase(onFetchCustomerOffPlan.rejected, (state, action: any) => {
      if (action.meta.aborted) return;
      state.customer.network.code = ServiceStatus.failed;
      state.customer.network.error = (action.payload as string) || 'Failed to fetch data';
    });

    builder.addCase(onFetchVerifyOffPlan.pending, (state) => {
      state.verify.network.code = ServiceStatus.loading;
    });
    builder.addCase(onFetchVerifyOffPlan.fulfilled, (state, action) => {
      state.verify.network.code = ServiceStatus.succeeded;
      if (action.payload != null) {
        state.verify.data = action.payload;
      }
    });
    builder.addCase(onFetchVerifyOffPlan.rejected, (state, action: any) => {
      if (action.meta.aborted) return;
      state.verify.network.code = ServiceStatus.failed;
      state.verify.network.error = (action.payload as string) || 'Failed to fetch data';
    });
  },
});

export const onFetchFilter = createAsyncThunk('off_plan/filter', async (param: any = {}, thunkAPI) => {
  try {
    const apiData = await service.fetchFilter(param, thunkAPI.signal);
    return apiData;
  } catch (error: any) {
    if (axios.isCancel(error)) throw error;
    // Handle any errors and return an error action
    return thunkAPI.rejectWithValue(error.response?.data?.message || error.message);
  }
});

export const onFetchCustomerOffPlan = createAsyncThunk('off_plan/customers', async (param: any, thunkAPI) => {
  try {
    const apiData = await service.fetchCustomerOffPlan(param, thunkAPI.signal);
    return { apiData, param };
  } catch (error: any) {
    if (axios.isCancel(error)) throw error;
    // Handle any errors and return an error action
    return thunkAPI.rejectWithValue(error.response?.data?.message || error.message);
  }
});

export const onFetchVerifyOffPlan = createAsyncThunk('off_plan/verify', async (params: any = {}, thunkAPI) => {
  try {
    const apiData = await service.fetchVerifyOffPlan(params, thunkAPI.signal);
    return apiData;
  } catch (error: any) {
    if (axios.isCancel(error)) throw error;
    return thunkAPI.rejectWithValue(error.response?.data?.message || error.message);
  }
});

// Actions
export const { patch, reset } = slice.actions;

// Export
export default slice.reducer;
